import { tr } from '@/lib/i18n-core';
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RESIDENT_KINDS, TAX_CATEGORIES, type BranchTaxRate, type ResidentKind, type TaxCategory } from '@his/shared';
import { get, put } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { todayIso } from '@/lib/utils';
import { TAX_CATEGORY_LABEL } from '@/components/TaxCategorySelect';
import { Alert, Button, Card, CardContent, CardHeader, CardTitle, DatePicker, Field, Input, Select, SkeletonText } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';

interface BranchTaxRatesView {
  branchId: string;
  country: string | null;
  regime: string;
  source: 'branch' | 'profile';
  effectiveFrom: string | null;
  rates: BranchTaxRate[];
  defaults: Omit<BranchTaxRate, 'effectiveFrom'>[];
  history: (BranchTaxRate & { id: string; createdAt: string })[];
}

type RateGrid = Record<string, string>;

const cell = (taxCategory: TaxCategory, residentKind: ResidentKind): string => `${taxCategory}|${residentKind}`;

function gridOf(view: BranchTaxRatesView | undefined): RateGrid {
  const grid: RateGrid = {};
  const source = view ? (view.rates.length ? view.rates : view.defaults) : [];
  for (const taxCategory of TAX_CATEGORIES) {
    for (const residentKind of RESIDENT_KINDS) {
      const row = source.find((rate) => rate.taxCategory === taxCategory && rate.residentKind === residentKind);
      grid[cell(taxCategory, residentKind)] = row ? String(row.pct) : '';
    }
  }
  return grid;
}

export function BranchTaxRatesTab({ readOnly }: { readOnly: boolean }) {
  const { user } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const branches = user?.branches ?? [];
  const [branchId, setBranchId] = useState<string>(branches[0]?.id ?? '');
  const [effectiveFrom, setEffectiveFrom] = useState(todayIso());
  const [grid, setGrid] = useState<RateGrid>({});

  const view = useQuery({
    queryKey: ['branch-tax-rates', branchId],
    queryFn: () => get<BranchTaxRatesView>(`/branches/${branchId}/tax-rates`),
    enabled: Boolean(branchId),
  });
  useEffect(() => setGrid(gridOf(view.data)), [view.data]);

  const save = useMutation({
    mutationFn: () =>
      put(`/branches/${branchId}/tax-rates`, {
        effectiveFrom,
        rates: TAX_CATEGORIES.flatMap((taxCategory) =>
          RESIDENT_KINDS.flatMap((residentKind) => {
            const text = grid[cell(taxCategory, residentKind)] ?? '';
            return text.trim() === '' ? [] : [{ taxCategory, residentKind, pct: Number(text) }];
          }),
        ),
      }),
    onSuccess: () => {
      toast.success(tr('billing', 'tax_rates_saved'));
      void queryClient.invalidateQueries({ queryKey: ['branch-tax-rates', branchId] });
    },
    onError: (error) => toast.error(tr('billing', 'could_not_save_the_tax_rates'), describeError(error)),
  });

  if (!branches.length) return <Alert>{tr('billing', 'no_rates_have_been_set_for_this_branch')}</Alert>;
  const data = view.data;
  const standardUnset = Boolean(data && !data.rates.some((rate) => rate.taxCategory === 'standard'));
  const invalid = Object.values(grid).some((text) => text.trim() !== '' && (!Number.isFinite(Number(text)) || Number(text) < 0 || Number(text) > 100));

  return (
    <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">{tr('billing', 'tax_rates')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">{tr('billing', 'tax_rates_by_branch_and_resident_kind')}</p>
          {branches.length > 1 && (
            <Field label={tr('billing', 'branch')}>
              <Select value={branchId} onChange={(event) => setBranchId(event.target.value)}>
                {branches.map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          {view.isLoading && <SkeletonText lines={4} />}
          {data && (
            <>
              {data.source === 'profile' && <Alert>{tr('billing', 'using_the_country_default_rates')}</Alert>}
              {data.source === 'branch' && standardUnset && <Alert>{tr('billing', 'each_service_keeps_its_own_rate_until_set')}</Alert>}
              {data.effectiveFrom && <p className="text-xs text-muted-foreground">{tr('billing', 'rates_in_force_from_x', { date: data.effectiveFrom })}</p>}
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-start text-xs text-muted-foreground">
                    <th className="py-1 pe-2 font-medium">{tr('billing', 'tax_category')}</th>
                    <th className="py-1 pe-2 font-medium">{tr('billing', 'citizen')} %</th>
                    <th className="py-1 font-medium">{tr('billing', 'non_citizen')} %</th>
                  </tr>
                </thead>
                <tbody>
                  {TAX_CATEGORIES.map((taxCategory) => (
                    <tr key={taxCategory} className="border-t border-border/60">
                      <td className="py-2 pe-2">{TAX_CATEGORY_LABEL[taxCategory]}</td>
                      {RESIDENT_KINDS.map((residentKind) => (
                        <td key={residentKind} className="py-2 pe-2">
                          <Input
                            type="number"
                            min={0}
                            max={100}
                            step={0.5}
                            disabled={readOnly}
                            value={grid[cell(taxCategory, residentKind)] ?? ''}
                            onChange={(event) => setGrid({ ...grid, [cell(taxCategory, residentKind)]: event.target.value })}
                            className="tabular w-24"
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {!readOnly && (
                <div className="flex flex-wrap items-end gap-3">
                  <Field label={tr('billing', 'applies_from')}>
                    <DatePicker value={effectiveFrom} onChange={setEffectiveFrom} />
                  </Field>
                  <Button onClick={() => save.mutate()} loading={save.isPending} disabled={invalid || !effectiveFrom}>
                    {tr('billing', 'save_tax_rates')}
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">{tr('billing', 'rate_history')}</CardTitle>
        </CardHeader>
        <CardContent>
          {data && data.history.length === 0 && <p className="text-sm text-muted-foreground">{tr('billing', 'no_rates_have_been_set_for_this_branch')}</p>}
          {data && data.history.length > 0 && (
            <ul className="space-y-1 text-sm">
              {data.history.map((row) => (
                <li key={row.id} className="flex justify-between gap-2">
                  <span>
                    {row.effectiveFrom} · {TAX_CATEGORY_LABEL[row.taxCategory]} · {row.residentKind === 'citizen' ? tr('billing', 'citizen') : tr('billing', 'non_citizen')}
                  </span>
                  <span className="tabular">{row.pct}%</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
